import { filter, takeUntil } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, Subject } from 'rxjs';
import { RequirePermission } from '@app/core/enums/require-permission.enum';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private oauthService = inject(OAuthService);


    private accessToken: string;
    public isAuthenticatedSubj = new BehaviorSubject<boolean>(null);

    private _userPermission: string[] = [];

    public configureAuth(takeUntilSubject: Subject<any>) {
        if (takeUntilSubject == null) {
            throw new Error('Please provide an unsubscribe event subject.');
        }

        this.oauthService.configure(environment.authConfig);
        this.oauthService.setStorage(localStorage);

        this.oauthService.events.pipe(
            filter(e => e.type === 'token_received'),
            takeUntil(takeUntilSubject))
            .subscribe(() => this.setAccessTokenField());
        
        this.oauthService.events.pipe(
            filter(e => e.type === 'token_expires'),
            takeUntil(takeUntilSubject))
            .subscribe(async () => await this.logout());
    }

    public async loadDiscoveryDocumentAndTryLogin(): Promise<void> {
        await this.oauthService.loadDiscoveryDocumentAndTryLogin();
        this.isAuthenticatedSubj.next(this.hasValidIdToken());
    }

    public async initImplicitFlow(): Promise<void> {
        this.oauthService.initImplicitFlow();
        return Promise.resolve();
    }

    public async logout(): Promise<void> {
        this.oauthService.logOut();
        this.clearAccessTokenField();
        return Promise.resolve();
    }

    public hasValidAccessToken(): boolean {
        const hasValidAccessToken = this.oauthService.hasValidAccessToken();

        if (hasValidAccessToken && this.accessToken == null) {
            this.setAccessTokenField();
        }
        return hasValidAccessToken;
    }

    public hasValidIdToken(): boolean {
        return this.oauthService.hasValidIdToken();
    }

    public getIdentityClaims(): Object {
        return this.oauthService.getIdentityClaims();
    }

    public get getAccessToken(): string {
        return this.oauthService.getAccessToken();
    }

    private setAccessTokenField(): void {
        this.accessToken = this.oauthService.getAccessToken();
    }

    private clearAccessTokenField(): void {
        this.accessToken = null;
    }

    public isAuthenticated() {
        return this.hasValidIdToken();
    }

    public getUserPermission(): string[] {
        if (this.isAuthenticated()) {
            if (!this._userPermission.length && this.getIdentityClaims()) {
                this._userPermission = (<any>this.getIdentityClaims()).permission as string[];
            }
            return this._userPermission;
        }
        return [];
    }

    public hasRole(roleName: string) {
        if (this.isAuthenticated()) {
           return (<any>this.getIdentityClaims()).role.includes(roleName);
        }

        return false;
    }

    havePermission(requiredPermissions: RequirePermission[]): boolean {
        let havePermission = false;
        if (this.isAuthenticated()) {
            if (!requiredPermissions ||
                (requiredPermissions?.length === 0)) {
                return true;
            }

            // verify if user have any required permission
            const userPermissions = this.getUserPermission();
            if (userPermissions) {
                userPermissions.forEach(userPer => {
                    if (requiredPermissions.includes(userPer as RequirePermission)) {
                        havePermission = true;
                    }
                });
            }
        }
        return havePermission;
    }
}
